// src/pages/parametrizacao/Parametrizacao.tsx
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPen, faGears, faStore, faStoreSlash, faBan } from '@/icons';
import KanbanColumn from '../../components/common/KanbanColumn';
import KanbanCard from '../../components/common/KanbanCard';
import { useTheme } from '../../contexts/ThemeContext';
import { Loja } from '../../types/parametrizacao/Loja';
import { PARAMETRIZACAO_OPCOES, ParametrizacaoOpcao } from '../../constants';
import '../../pages/parametrizacao/Parametrizacao.css';
import { useAuth } from '../../contexts/AuthContext';
import { useEntitlement } from '../../contexts/EntitlementContext';
import SidePanel from '../../components/common/SidePanel';
import CreateEditLojaAcessoSidePanelContent from './CreateEditLojaAcessoSidePanelContent';
import RegrasAprovacaoPanel from './RegrasAprovacaoPanel';
import LogoEmailPanel from './LogoEmailPanel';
import ConfiguracaoLoginPanel from './ConfiguracaoLoginPanel';
import { useParametrizacaoKanban } from '../../hooks/useParametrizacaoKanban';
import { useLojaMutations } from '../../hooks/useLojaMutations';

const Parametrizacao = () => {
  const { theme } = useTheme();
  const { hasPermission, role } = useAuth();
  // Gate de módulo (sem bypass de MASTER): a opção/parametrização "Requisições"
  // pertence ao PO. Com PO inativo, some do select e o botão "Regras de aprovação"
  // não carrega (logo o RegrasAprovacaoPanel e seu GET nunca disparam).
  const { isAreaEnabled } = useEntitlement();
  const requisicoesParamLiberada = isAreaEnabled('requisicoes');
  const semCadastroSentinelRef = useRef<HTMLDivElement>(null);
  const cadastradaSentinelRef = useRef<HTMLDivElement>(null);
  const inativasSentinelRef = useRef<HTMLDivElement>(null);
  const [showRegrasAprovacao, setShowRegrasAprovacao] = useState(false);
  const [showLogoEmail, setShowLogoEmail] = useState(false);
  // Painel de método de login do tenant (issue #28). Hook declarado aqui, antes
  // do early-return de permissão, para não reintroduzir o bug de rules-of-hooks
  // (ADR-010).
  const [showLoginConfig, setShowLoginConfig] = useState(false);

  // Inativar/reativar loja exige a mesma permissão de editar (MASTER bypassa).
  // Sem ela, os cards de Cadastrada/Inativas não são arrastáveis (o backend
  // também bloqueia — isto é só UX).
  const podeEditarLoja = role === 'ROLE_MASTER' || hasPermission('lojas_acesso.editar');

  const kanbans = useParametrizacaoKanban();
  const mutations = useLojaMutations({ onReload: kanbans.reloadColumns });

  const handleLojasClick = () => {
    setShowRegrasAprovacao(false);
    setShowLogoEmail(false);
    setShowLoginConfig(false);
    kanbans.setShowKanban(true);
  };

  const handleRegrasAprovacaoClick = () => {
    kanbans.setShowKanban(false);
    mutations.handleCancelForm();
    setShowLogoEmail(false);
    setShowLoginConfig(false);
    setShowRegrasAprovacao(true);
  };

  const handleLogoEmailClick = () => {
    kanbans.setShowKanban(false);
    mutations.handleCancelForm();
    setShowRegrasAprovacao(false);
    setShowLoginConfig(false);
    setShowLogoEmail(true);
  };

  const handleLoginConfigClick = () => {
    kanbans.setShowKanban(false);
    mutations.handleCancelForm();
    setShowRegrasAprovacao(false);
    setShowLogoEmail(false);
    setShowLoginConfig(true);
  };

  // Mapeamento de opções para botões.
  const opcoesBotoesMap: Record<ParametrizacaoOpcao, { label: string; onClick: () => void }[]> = {
    Integrações: [
      ...(hasPermission('lojas_acesso.listar') ? [{ label: 'Lojas', onClick: handleLojasClick }] : []),
    ],
    Requisições: [
      ...(requisicoesParamLiberada && hasPermission('configuration.listar')
        ? [{ label: 'Regras de aprovação', onClick: handleRegrasAprovacaoClick }]
        : []),
    ],
    Personalização: [
      ...(hasPermission('configuration.listar')
        ? [{ label: 'Logo de E-mail', onClick: handleLogoEmailClick }]
        : []),
    ],
    Usuários: [
      // Permissão dedicada ao método de login (issue #30 / backend #67) — separada
      // das genéricas configuration.listar/editar usadas acima.
      ...(hasPermission('configuration.listar_login')
        ? [{ label: 'Login', onClick: handleLoginConfigClick }]
        : []),
    ],
  };

  useEffect(() => {
    document.title = 'Bluelt | Parametrizações';
  }, []);

  useEffect(() => {
    if (!kanbans.showKanban || kanbans.semCadastro.items.length === 0) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !kanbans.semCadastro.isLoading) {
          kanbans.semCadastro.loadMore();
        }
      },
      { threshold: 0.1, rootMargin: '0px 0px 50px 0px' }
    );
    const currentRef = semCadastroSentinelRef.current;
    if (currentRef) observer.observe(currentRef);
    return () => {
      if (currentRef) observer.unobserve(currentRef);
    };
  }, [kanbans.showKanban, kanbans.semCadastro.items, kanbans.semCadastro.isLoading, kanbans.semCadastro.loadMore]);

  useEffect(() => {
    if (!kanbans.showKanban || kanbans.cadastrada.items.length === 0) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !kanbans.cadastrada.isLoading) {
          kanbans.cadastrada.loadMore();
        }
      },
      { threshold: 0.1, rootMargin: '0px 0px 50px 0px' }
    );
    const currentRef = cadastradaSentinelRef.current;
    if (currentRef) observer.observe(currentRef);
    return () => {
      if (currentRef) observer.unobserve(currentRef);
    };
  }, [kanbans.showKanban, kanbans.cadastrada.items, kanbans.cadastrada.isLoading, kanbans.cadastrada.loadMore]);

  useEffect(() => {
    if (!kanbans.showKanban || kanbans.inativas.items.length === 0) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !kanbans.inativas.isLoading) {
          kanbans.inativas.loadMore();
        }
      },
      { threshold: 0.1, rootMargin: '0px 0px 50px 0px' }
    );
    const currentRef = inativasSentinelRef.current;
    if (currentRef) observer.observe(currentRef);
    return () => {
      if (currentRef) observer.unobserve(currentRef);
    };
  }, [kanbans.showKanban, kanbans.inativas.items, kanbans.inativas.isLoading, kanbans.inativas.loadMore]);

  // useCallbacks declarados antes do early-return de permissao para satisfazer
  // regras de hooks (ADR-010 — fix do bug latente do APP-BLUELTSYSTEM-1 que
  // dispara se hasPermission mudar runtime no mesmo mount).
  const renderSemCadastroItem = useCallback((loja: Loja) => (
    <KanbanCard<Loja>
      item={loja}
      renderContent={(l) => (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <strong>Código Filial: {l.codFilial}</strong>
          <div className="kanban-item-force-integration-card-itens">Nome Fantasia: {l.nomeFantasia}</div>
          <div className="kanban-item-force-integration-card-itens">CNPJ: {l.cnpj}</div>
          <div className="kanban-item-force-integration-card-itens">Razão Social: {l.razaoSocial}</div>
        </div>
      )}
      isDraggable={true}
      onDragStart={mutations.onDragStartSemCadastro}
    />
  ), [mutations.onDragStartSemCadastro]);

  const renderCadastradaItem = useCallback((loja: Loja) => (
    <KanbanCard<Loja>
      item={loja}
      renderContent={(l) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
          <strong>Código Filial: {l.codFilial}</strong>
          <div>Nome Fantasia: {l.nomeFantasia}</div>
          <div>CNPJ: {l.cnpj}</div>
          <div>Razão Social: {l.razaoSocial}</div>
        </div>
      )}
      isDraggable={podeEditarLoja}
      onDragStart={mutations.onDragStartCadastrada}
      actionButton={
        podeEditarLoja
          ? {
              icon: faPen,
              onClick: mutations.handleEditClick,
              title: 'Editar Loja',
              className: 'edit-button',
            }
          : undefined
      }
    />
  ), [mutations.onDragStartCadastrada, mutations.handleEditClick, podeEditarLoja]);

  // Cards de Lojas Inativas: arrastáveis de volta p/ Cadastrada (reativar), SEM
  // botão Editar e SEM side panel (decisão do dev). Só arrastável com permissão.
  const renderInativaItem = useCallback((loja: Loja) => (
    <KanbanCard<Loja>
      item={loja}
      renderContent={(l) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
          <strong>Código Filial: {l.codFilial}</strong>
          <div>Nome Fantasia: {l.nomeFantasia}</div>
          <div>CNPJ: {l.cnpj}</div>
          <div>Razão Social: {l.razaoSocial}</div>
        </div>
      )}
      isDraggable={podeEditarLoja}
      onDragStart={mutations.onDragStartInativas}
    />
  ), [mutations.onDragStartInativas, podeEditarLoja]);

  const requiredPermission = 'modulo.parametrizacao';
  if (!hasPermission(requiredPermission)) {
    return <div>Você não tem permissão para acessar esta página.</div>;
  }

  const handleChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    kanbans.setSelectedOption(event.target.value as ParametrizacaoOpcao | '');
    kanbans.setShowKanban(false);
    setShowRegrasAprovacao(false);
    setShowLogoEmail(false);
    setShowLoginConfig(false);
    mutations.handleCancelForm();
  };

  const kanbanWidth = mutations.showForm ? '66%' : '100%';

  return (
    <div className={`${theme}`}>
      <main className="parametrizacao-main">
        <h1>
          <FontAwesomeIcon icon={faGears} /> Parametrização
        </h1>
        <div className="header-actions">
          <select
            id="parametrizacao-options"
            value={kanbans.selectedOption}
            onChange={handleChange}
            className="parametrizacao-select"
          >
            <option value="">Selecione...</option>
            {PARAMETRIZACAO_OPCOES.filter(
              // "Requisições" é gateada por PO; demais opções sempre visíveis.
              (opcao) => opcao !== 'Requisições' || requisicoesParamLiberada
            ).map((opcao: ParametrizacaoOpcao) => (
              <option key={opcao} value={opcao}>
                {opcao}
              </option>
            ))}
          </select>
        </div>
      </main>
      <div className="parametrizacao-content">
        {kanbans.selectedOption && opcoesBotoesMap[kanbans.selectedOption as ParametrizacaoOpcao] && (
          <div className="options-container">
            {opcoesBotoesMap[kanbans.selectedOption as ParametrizacaoOpcao].map((botao) => (
              <button key={botao.label} onClick={botao.onClick} className="option-button">
                {botao.label}
              </button>
            ))}
          </div>
        )}
        {kanbans.showKanban && (
          <div className="kanban-container" style={{ width: kanbanWidth }}>
            <KanbanColumn<Loja>
              title="Loja Não Cadastrada"
              icon={faStoreSlash}
              data={kanbans.semCadastro.items}
              renderItem={renderSemCadastroItem}
              onDragOver={mutations.onDragOver}
              onDrop={(e) => mutations.onDrop(e, 'semCadastro')}
              ref={semCadastroSentinelRef}
            />
            <KanbanColumn<Loja>
              title="Loja Cadastrada"
              icon={faStore}
              data={kanbans.cadastrada.items}
              renderItem={renderCadastradaItem}
              onDragOver={mutations.onDragOver}
              onDrop={(e) => mutations.onDrop(e, 'cadastrada')}
              ref={cadastradaSentinelRef}
            />
            <KanbanColumn<Loja>
              title="Lojas Inativas"
              icon={faBan}
              data={kanbans.inativas.items}
              renderItem={renderInativaItem}
              onDragOver={mutations.onDragOver}
              onDrop={(e) => mutations.onDrop(e, 'inativas')}
              ref={inativasSentinelRef}
            />
          </div>
        )}
        {kanbans.showKanban && (
          <SidePanel isOpen={mutations.showForm} onClose={mutations.handleCancelForm} theme={theme}>
            {mutations.showForm && mutations.selectedLoja && (
              <CreateEditLojaAcessoSidePanelContent
                selectedLoja={mutations.selectedLoja}
                isLoading={mutations.isSaving}
                handleFormChange={mutations.handleFormChange}
                handleSave={mutations.handleSave}
                handleCancel={mutations.handleCancelForm}
              />
            )}
          </SidePanel>
        )}
        {showRegrasAprovacao && <RegrasAprovacaoPanel theme={theme} />}
        {showLogoEmail && <LogoEmailPanel theme={theme} />}
        {showLoginConfig && <ConfiguracaoLoginPanel theme={theme} />}
      </div>
    </div>
  );
};

export default Parametrizacao;